/**
 * Allow-list of hostnames that may be saved as snapfit `meeting_urls`.
 *
 * The validation is intentionally permissive on subdomains (uses
 * `hostname.includes(domain)`) so e.g. tenant-specific Microsoft Bookings
 * URLs that nest under `outlook.office.com` still match.
 *
 * Source of truth — both ScanSetupView (ForthStep) and TargetGroupSettings
 * import from here so the lists never drift apart.
 *
 * Backend (`backend-api`) does NOT validate `meeting_urls`; it stores whatever
 * is sent. SnapFit-scan opens any URL through `MeetingRedirectModal` (or the
 * Calendly popup for `calendly.com` URLs), so adding a new domain here is
 * sufficient end-to-end — no other code changes are required.
 */
export const ALLOWED_MEETING_DOMAINS = [
    'calendly.com',
    'cal.com',
    'calendar.google.com',
    'meet.google.com',
    'outlook.office365.com',
    'outlook.office.com',
    'teams.microsoft.com',
    'book.ms',
];

/**
 * Human-readable list shown under the "Meeting URLs" input. Kept alongside
 * the domain list so updating one without the other is not possible.
 */
export const SUPPORTED_MEETING_PLATFORMS_LABEL =
    'Supported: Calendly, Cal.com, Google Calendar, Google Meet, Microsoft Outlook, Microsoft Bookings, Microsoft Teams';

/**
 * Returns true when `url` is an HTTPS URL whose hostname matches one of the
 * `ALLOWED_MEETING_DOMAINS`. Empty / malformed strings return false.
 */
export const isValidMeetingUrl = (url: string): boolean => {
    try {
        const parsed = new URL(url);
        return (
            parsed.protocol === 'https:' &&
            ALLOWED_MEETING_DOMAINS.some((d) => parsed.hostname.includes(d))
        );
    } catch {
        return false;
    }
};
